import api from "./api";

const getAuthHeaders = (token: string | null) => ({
  Authorization: `Bearer ${token || ""}`,
});

export interface FormConfigField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
}

export interface FederationFormConfig {
  baseFieldRequirements: Record<string, boolean>;
  extraBaseFields: FormConfigField[];
  customFields: FormConfigField[];
}

export interface FederationInfo {
  tenant: {
    id: string;
    slug: string;
    name: string;
    fullName: string | null;
    logoUrl: string | null;
    primaryColor: string | null;
    /** @deprecated requireMinorCpf removed — CPF is always required for minors */
    minorConfig?: { requireMinorCpf?: boolean };
  };
  academies: Array<{
    id: string;
    name: string;
    city: string | null;
    state: string | null;
  }>;
  pricingTables: Array<{
    id: string;
    name: string;
    priceCents: number;
    category: string;
    fieldKey?: string | null;
    isRecurring: boolean;
    eligibility?: {
      minAge?: number;
      maxAge?: number;
      belts?: string[];
      description?: string;
    } | null;
  }>;
  formConfig: FederationFormConfig;
}

export interface VerifyResult {
  verified: boolean;
  reason?: string;
  message?: string;
  affiliationId?: string;
  athleteUuid?: string;
  name?: string;
  cpf?: string;
  birthDate?: string;
  belt?: string;
  customFields?: Record<string, unknown>;
  academy?: { id: string; name: string } | null;
  expiresAt?: string;
  federationSlug?: string;
  isMinor?: boolean;
  status?: string;
  hasAccount?: boolean;
}

export interface LookupResult {
  found: boolean;
  affiliation: {
    id: string;
    name: string;
    cpf: string | null;
    email: string | null;
    status: string;
    expiresAt: string | null;
    belt: string | null;
    academy: { id: string; name: string } | null;
  } | null;
}

/**
 * Get federation info (tenant, academies, pricing).
 */
export async function getFederationInfo(
  slug: string,
  token: string | null
): Promise<FederationInfo> {
  const { data } = await api.get(`/federations/integration/${slug}`, {
    headers: getAuthHeaders(token),
  });
  return data;
}

/**
 * Look up athlete by CPF in a federation.
 */
export async function lookupFederationAthlete(
  slug: string,
  cpf: string,
  token: string | null
): Promise<LookupResult> {
  const { data } = await api.get(
    `/federations/integration/${slug}/lookup?cpf=${cpf.replace(/\D/g, "")}`,
    { headers: getAuthHeaders(token) }
  );
  return data;
}

/**
 * Verify athlete's active affiliation in a federation.
 */
export async function verifyFederationAffiliation(
  slug: string,
  cpf: string,
  fighterId: number | undefined,
  token: string | null,
  force?: boolean,
  checkEmail?: string
): Promise<VerifyResult> {
  const query = force ? "?force=true" : "";
  const { data } = await api.post(
    `/federations/integration/${slug}/verify${query}`,
    { cpf: cpf.replace(/\D/g, ""), fighterId, ...(checkEmail && { checkEmail }) },
    { headers: getAuthHeaders(token) }
  );
  return data;
}

export interface CreateAffiliationParams {
  cpf: string;
  name: string;
  email: string;
  phone?: string;
  birthDate?: string;
  academyId?: string;
  pricingTableId?: string;
  belt?: string;
  fighterId?: number;
  photoUrl?: string;
  documentUrl?: string;
  customFields?: Record<string, unknown>;
  isMinor?: boolean;
  guardianName?: string;
  guardianCpf?: string;
  guardianPhone?: string;
  guardianEmail?: string;
  ownerPassword?: string;
}

export interface CreateAffiliationResult {
  affiliationId: string;
  userId: string;
  status: string;
  createdAt: string;
  requiresPayment: boolean;
  priceCents: number | null;
}

export interface PixPaymentResult {
  paymentId: string;
  amount: number;
  pixQrCode: string;
  pixQrCodeImage: string;
  pixExpiresAt: string;
  gatewayOrderId: string;
}

export interface AffiliationStatusResult {
  affiliation: {
    id: string;
    name: string;
    cpf: string | null;
    status: string;
    expiresAt: string | null;
    rejectionReason: string | null;
    belt: string | null;
    customFields?: Record<string, unknown>;
    academy: { id: string; name: string } | null;
    payments: Array<{
      id: string;
      amount: number;
      status: string;
      paidAt: string | null;
      pixQrCode: string | null;
      pixQrCodeImage: string | null;
      pixExpiresAt: string | null;
    }>;
  };
}

/**
 * Create a new affiliation in a federation.
 */
export async function createFederationAffiliation(
  slug: string,
  params: CreateAffiliationParams,
  token: string | null
): Promise<CreateAffiliationResult> {
  const { data } = await api.post(
    `/federations/integration/${slug}/affiliations`,
    params,
    { headers: getAuthHeaders(token) }
  );
  return data;
}

export interface RenewAffiliationResult {
  affiliationId: string;
  status: string;
  requiresPayment: boolean;
  priceCents: number;
  message?: string;
}

/**
 * Renew an expired affiliation in a federation.
 */
export async function renewFederationAffiliation(
  slug: string,
  affiliationId: string,
  pricingTableId: string,
  token: string | null
): Promise<RenewAffiliationResult> {
  const { data } = await api.post(
    `/federations/integration/${slug}/affiliations/${affiliationId}/renew`,
    { pricingTableId },
    { headers: getAuthHeaders(token) }
  );
  return data;
}

/**
 * Generate PIX payment for a pending affiliation.
 */
export async function createFederationPixPayment(
  slug: string,
  affiliationId: string,
  pricingTableId: string,
  token: string | null
): Promise<PixPaymentResult> {
  const { data } = await api.post(
    `/federations/integration/${slug}/affiliations/${affiliationId}/pix`,
    { pricingTableId },
    { headers: getAuthHeaders(token) }
  );
  return data;
}

export interface UpdateAffiliationParams {
  name?: string;
  email?: string;
  phone?: string;
  birthDate?: string;
  photoUrl?: string;
  documentUrl?: string;
  academyId?: string;
  customFields?: Record<string, unknown>;
}

export interface UpdateAffiliationResult {
  updated: boolean;
  requiresPayment: boolean;
  requiresApproval?: boolean;
  priceCents?: number;
  pricingTableId?: string;
  affiliationId: string;
  changedFields?: string[];
  message?: string;
}

/**
 * Update affiliation data in a federation.
 */
export async function updateFederationAffiliation(
  slug: string,
  affiliationId: string,
  params: UpdateAffiliationParams,
  token: string | null
): Promise<UpdateAffiliationResult> {
  const { data } = await api.patch(
    `/federations/integration/${slug}/affiliations/${affiliationId}`,
    params,
    { headers: getAuthHeaders(token) }
  );
  return data;
}

/**
 * Get federation pricing tables by category.
 */
export async function getFederationPricing(
  slug: string,
  category: string,
  token: string | null
): Promise<{
  pricingTables: Array<{
    id: string;
    name: string;
    priceCents: number;
    category: string;
    fieldKey?: string | null;
    isRecurring: boolean;
  }>;
}> {
  const info = await getFederationInfo(slug, token);
  return {
    pricingTables: info.pricingTables.filter((p) => p.category === category),
  };
}

/**
 * Get affiliation status (for polling).
 */
export async function getFederationAffiliationStatus(
  slug: string,
  affiliationId: string,
  token: string | null
): Promise<AffiliationStatusResult> {
  const { data } = await api.get(
    `/federations/integration/${slug}/affiliations/${affiliationId}`,
    { headers: getAuthHeaders(token) }
  );
  return data;
}
